import clsx from 'clsx'
import { Portal } from 'components/utility/portal'
import { AppToastContainer, showErrorToast } from 'components/utility/toast'
import React, { useEffect, useState } from 'react'
import { selectRequirementTitle, usePackageStore } from 'stores/packageStore'
import { useFlagActions } from '../hooks/useFlagActions'
import RequirementFlagGroup from './RequirementFlagGroup'
import flagPlusIcon from '/app/assets/images/icons/flag-plus.svg'

const CLOSED_STATE = { open: false, requirementId: null, documentId: null, flags: [] }

export default function LockConfirmationModal({ packageId }) {
  const [state, setState] = useState(CLOSED_STATE)
  const [confirming, setConfirming] = useState(false)
  const { accept, remove } = useFlagActions(packageId)
  const requirementName = usePackageStore(selectRequirementTitle(state.requirementId))

  useEffect(() => {
    const handler = (e) => {
      const { requirementId, flags } = e.detail
      setState({ open: true, requirementId, flags })
    }

    document.addEventListener('package:lock-confirm', handler)
    return () => document.removeEventListener('package:lock-confirm', handler)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('overlay-activated', state.open)
    return () => {
      document.body.classList.remove('overlay-activated')
    }
  }, [state.open])

  const hasDraftedFlags = state.flags.some((f) => f.state === 'drafted')

  const handleCancel = () => setState(CLOSED_STATE)

  const handleConfirm = async () => {
    const { flags, requirementId } = state
    const { fixed, drafted } = flags.reduce(
      (acc, f) => {
        if (f.state === 'fixed') acc.fixed.push(f)
        else if (f.state === 'drafted') acc.drafted.push(f)
        return acc
      },
      { fixed: [], drafted: [] }
    )

    setConfirming(true)

    try {
      await Promise.all([...fixed.map((f) => accept(f.id)), ...drafted.map((f) => remove(f.id))])
      setState(CLOSED_STATE)
      document.dispatchEvent(new CustomEvent('package:lock-confirm-proceed', { detail: { requirementId } }))
    } catch (error) {
      showErrorToast({ message: 'Something went wrong' })
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Portal>
      <div className={clsx('lock-confirmation-overlay dome-full-width dome-full-height', state.open && 'show')}>
        <div className="dome-full-width dome-full-height dome-d-flex dome-align-center dome-justify-center">
          <div className="lock-confirmation-modal dome-d-flex dome-flex-column dome-gap24">
            <div className="dome-d-flex dome-flex-column dome-gap12">
              <div>
                <img src={flagPlusIcon} />
              </div>
              <div className="dome-h2 dome-color-dark-green">resolve requirement flags?</div>
            </div>
            <div>
              <div className="dome-p1 dome-color-dark-grey">
                Completing this section will also clear the following flags that have been marked.
              </div>
              {hasDraftedFlags && (
                <div className="unshared-flags-warning">
                  New flags added after re-submission have not been shared with applicants yet. Resolving all
                  requirement flags will remove any new flags.
                </div>
              )}
            </div>
            <div className='lock-all-requirement-list'>
              <RequirementFlagGroup requirement={{ requirementName, flags: state.flags }} />
            </div>
            <div className="modal-footnote">
              <div className="dome-color-dark-grey dome-text-style-italic">
                You can view flag history by clicking into the requirement to file and using the &ldquo;finished&rdquo;
                tab.
              </div>
            </div>
            <div className="modal-actions">
              <button className="dome-btn dome-btn-link dome-btn-link-back" onClick={handleCancel}>
                back
              </button>
              <button
                className={clsx(
                  'dome-btn dome-btn-base',
                  hasDraftedFlags ? 'dome-btn-red-stroke' : 'dome-btn-dark-green'
                )}
                onClick={handleConfirm}
                disabled={confirming}
              >
                yes, complete
              </button>
            </div>
          </div>
        </div>
      </div>
      <AppToastContainer />
    </Portal>
  )
}
